import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  type IProductProperty,
  type IProperty,
  type IPropertyType,
} from '@mahawi/eshop-common/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
} from '../async-state';
import { type IPropertiesState } from './types';

export const preloadedState: IPropertiesState = {
  inProcess: false,
  codes: [],
  types: [],
  product: {
    properties: undefined,
  },
  property: {
    code: undefined,
  },
  properties: undefined,
  updatedAt: undefined,
};

const propertiesSlice = createSlice({
  name: 'Properties',
  initialState: preloadedState,
  reducers: {
    propertyAdd: {
      reducer(state) {
        state.inProcess = true;
        state.property = { code: undefined };
      },
      prepare(code: string) {
        return { payload: { code } };
      },
    },
    propertyAddResponse: {
      reducer(state, action: { payload: { property: { code: string } } }) {
        state.codes.push(action.payload.property.code);
        state.codes.sort((a: string, b: string): number => stringCompare(a, b));
        state.inProcess = false;
        state.property = action.payload.property;
        state.updatedAt = Date.now();
      },
      prepare(code: string) {
        return { payload: { property: { code } } };
      },
    },
    propertiesLoad(state) {
      state.inProcess = true;
    },
    propertiesLoadResponse: {
      reducer(state, action: { payload: { property: { codes: string[] } } }) {
        state.codes = action.payload.property.codes.sort(
          (a: string, b: string): number => stringCompare(a, b),
        );
        state.inProcess = false;
        state.updatedAt = Date.now();
      },
      prepare(codes: string[]) {
        return { payload: { property: { codes } } };
      },
    },
    propertiesLoadAllTypes(state) {
      state.inProcess = true;
    },
    propertiesLoadAllTypesResponse: {
      reducer(state, action: { payload: { types: IPropertyType[] } }) {
        state.types = action.payload.types;
        state.inProcess = false;
        state.updatedAt = Date.now();
      },
      prepare(types: IPropertyType[]) {
        return { payload: { types } };
      },
    },
    loadProductProperties: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productUUID: string) {
        return { payload: { product: { uuid: productUUID } } };
      },
    },
    loadProductPropertiesResponse: {
      reducer(state, action: { payload: { properties: IProductProperty[] } }) {
        state.product.properties = action.payload.properties;
        state.inProcess = false;
        state.updatedAt = Date.now();
      },
      prepare(properties: IProductProperty[]) {
        return { payload: { properties } };
      },
    },
    loadPropertiesByRawNames: {
      reducer(state) {
        state.inProcess = true;
        state.properties = undefined;
      },
      prepare(rawNames: string[]) {
        return { payload: { rawNames } };
      },
    },
    loadPropertiesByRawNamesResponse: {
      reducer(state, action: { payload: { properties: IProperty[] } }) {
        state.properties = action.payload.properties;
        state.inProcess = false;
        state.updatedAt = Date.now();
      },
      prepare(properties: IProperty[]) {
        return { payload: { properties } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('Properties'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  propertyAdd,
  propertyAddResponse,
  propertiesLoad,
  propertiesLoadResponse,
  propertiesLoadAllTypes,
  propertiesLoadAllTypesResponse,
  loadProductProperties,
  loadProductPropertiesResponse,
  loadPropertiesByRawNames,
  loadPropertiesByRawNamesResponse,
} = propertiesSlice.actions;
export default propertiesSlice.reducer;
